// Validating a search param makes its type a truth (§4.4); domain-bound parsers stay with their
// route. Twin of the frontend's: apps/frontend/docs/razionale-che-il-codice-indica.md

// A positive integer, or null. `/^\d+$/` and not `Number()`, which accepts '0x10', '1e3',
// ' 7 ' and '' (that one becomes 0); ids start at 1 (Identity(always=True)).
export function parsePositiveInt(value: unknown): number | null {
  const text = typeof value === 'number' ? String(value) : value
  if (typeof text !== 'string' || !/^\d+$/.test(text)) return null
  const parsed = Number.parseInt(text, 10)
  return parsed >= 1 ? parsed : null
}

/** A card's `$id` param, both directions. `parse` returning `false` means "this route does
 * not match": we fall through to notFoundComponent and send nothing, so `/clubs/0x10`
 * cannot open club 16. `stringify` is what makes `params={{ id: 7 }}` type-check. */
export const numericIdParam = {
  parse: (raw: Record<string, string>): { readonly id: number } | false => {
    const id = parsePositiveInt(raw.id)
    return id === null ? false : { id }
  },
  stringify: ({ id }: { readonly id: number }) => ({ id: String(id) }),
}

/** The page from the URL, 1-based. Anything that is not a positive integer is page 1. */
export function parsePage(value: unknown): number {
  return parsePositiveInt(value) ?? 1
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** A civil day, `2026-11-02`, or null. The shape only: a day the calendar lacks fails later, at
 *  the API, like any other bad bound. */
export function parseIsoDay(value: unknown): string | null {
  return typeof value === 'string' && ISO_DAY.test(value) ? value : null
}

/** The searched text. An empty or whitespace-only string is not a search. */
export function parseQuery(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const cleaned = value.trim()
  return cleaned === '' ? null : cleaned
}

// A value validated against a closed list, or null: an invented one becomes "no filter"
// instead of reaching the backend, where it would 422 a mistyped link.
export function parseOneOf<TValue extends string>(
  values: readonly TValue[],
  value: unknown,
): TValue | null {
  if (typeof value !== 'string') return null
  return values.includes(value as TValue) ? (value as TValue) : null
}

// `parseOneOf` for a param whose absence is the normal case: `undefined` rather than `null`,
// because these end up in an optional search field.
export function parseOptionalOneOf<TValue extends string>(
  values: readonly TValue[],
  value: unknown,
): TValue | undefined {
  return parseOneOf(values, value) ?? undefined
}

// Routes that cannot be a destination — their own list because a pure validator has no router
// to ask. `/sign-in` would nest itself through `returnTo`; `/forgot-password` ends in one.
const NOT_A_DESTINATION = ['/sign-in', '/forgot-password']

// A relative path within this app, and not an auth page. Without the first check
// `?returnTo=https://elsewhere.example` is an open redirect; `//host` reads as absolute.
export function parseReturnTo(value: unknown): string {
  if (typeof value !== 'string') return '/'
  if (!value.startsWith('/') || value.startsWith('//')) return '/'
  if (NOT_A_DESTINATION.includes(pathOf(value))) return '/'
  return value
}

/** The path alone, so the comparison lands on a boundary: `/sign-in-help` is a destination. */
export function pathOf(url: string): string {
  return url.split(/[?#]/)[0] ?? ''
}
